import FilterConfiguration, { isFieldFilterable } from './FilterConfiguration';
import { FilterPopover, FilterPopoverContent, FilterPopoverWrapped} from './FilterPopover'
import { useAct } from 'DashboardUser/store'
import restClient from 'Shared/hooks/restClient'
import OrganisationPickerModal from 'Shared/components/OrganisationPicker/OrganisationPickerModal'

export default function EditFilterPill({filterKey}) {
    const act = useAct();

    const [isOpen, setIsOpen] = useState(false)
    const [orgPickerOpen, setOrgPickerOpen] = useState(false)
    const dashboardField = useSel(s => s.dashboard.dashboardFields.find(f => 'field_' + f.id === filterKey) ?? null);
    const questions = useSel(s => s.dashboard.survey.questions);
    const filter = useSel(s => s.local.filters ? s.local.filters[filterKey] : null);
    const sortingField = useSel(s => s.local.sorting?.fieldId);
    const sortingDirection = useSel(s => s.local.sorting?.direction);
    const question = questions ? questions.find(q => q.id === dashboardField?.questionId) : null;
    const [organisationName, setOrganisationName] = useState(null);

    const isOrgFilter = dashboardField?.source === 'organisation' || filterKey === 'organisation_id';

    useEffect(() => {
        if (isOrgFilter && filter && filter !== '[present]' && filter !== '[blank]') {
            restClient.get(`/api/organisations/${filter}`)
                .then(({ data }) => setOrganisationName(data.name))
                .catch(() => setOrganisationName(null));
        } else {
            setOrganisationName(null);
        }
    }, [isOrgFilter, filter]);

    const closePopup = () => {
        setIsOpen(false);
    }

    const StringFilterLabel = () => {
        if (filter === '[present]') return 'has value';
        if (filter === '[blank]') return 'is missing/empty';
        return 'contains "' + filter + '"';
    }

    const BooleanFilterLabel = () => {
        switch(dashboardField?.source) {
            case 'search': return filter === '[true]' ? 'Yes' : 'No';
            case 'goal': return filter === '[true]' ? 'completed goal' : 'not completed goal';
            case 'subscription':
            case 'resubscription': return filter === '[true]' ? 'is subscribed' : 'not subscribed';
            default: return filter === '[true]' ? 'Yes' : 'No';
        }
    }

    const AnswerFilterLabel = () => {
        if (filter === '[present]') return 'answered';
        if (filter === '[blank]') return 'not answered';

        const answer = question?.answers?.find(option => String(option.id) === String(filter));
        return answer ? 'is "' + answer.answer + '"' : 'selected answer';
    }

    const removeFilter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        closePopup();
        setOrgPickerOpen(false);
        act.local.setFilter(filterKey, null);
        if (sortingField === dashboardField?.id) {
            act.local.setSorting(null, null);
        }
    }

    const FilterLabelTitle = () => {
        if (filterKey === 'organisation_id') return 'Organisation';
        return dashboardField?.name || 'Filter';
    }

    const FilterLabel = () => {
        if (filterKey === 'organisation_id') {
            return organisationName ? `is "${organisationName}"` : 'selected organisation';
        }
        if (sortingField && ('field_' + sortingField === filterKey && !filter)) {
            return sortingDirection === 'asc' ? 'sorted A-Z' : 'sorted Z-A';
        }
        switch (dashboardField?.source) {
            case 'column': return StringFilterLabel();
            case 'search':
            case 'goal':
            case 'subscription':
            case 'resubscription':
                return BooleanFilterLabel();
            case 'organisation':
                if (filter === '[present]') return 'has organisation';
                if (filter === '[blank]') return 'missing organisation';
                return organisationName ? `is "${organisationName}"` : 'selected organisation';
            case 'survey_response':
            case 'answerable_question':
                switch (question?.questionType) {
                    case 'checkbox': return BooleanFilterLabel();
                    case 'text': return StringFilterLabel();
                    case 'multiple_choice':
                    case 'multiple_select':
                        return AnswerFilterLabel();
                    default:
                        return 'Unsupported survey question type (' + question?.questionType + ')';
                }
        }
        return 'Unsupported filter source (' + dashboardField?.source + ')';
    }

    const isEditable = filterKey === 'organisation_id' || isFieldFilterable(dashboardField, questions);

    const sortingSetDirection = sortingField === dashboardField?.id ? sortingDirection : null;

    const pillBody = <>
        <div>
            <span className="pill-label-with-sort">
                {sortingSetDirection && <SvgIconsSort asc={sortingSetDirection === 'asc'} desc={sortingSetDirection === 'desc'} />}
                <span>
                    <span className="bold"><FilterLabelTitle />:</span> <FilterLabel />
                </span>
            </span>
        </div>
        <div className="pill-close">
            <a href="#" onClick={removeFilter}><SvgIconsClose /></a>
        </div>
    </>;

    if (!isEditable) {
        return <div className="pill non-editable">{pillBody}</div>;
    }

    if (filterKey === 'organisation_id') {
        return <>
            <div className="pill" onClick={() => setOrgPickerOpen(true)}>
                {pillBody}
            </div>
            {orgPickerOpen && <OrganisationPickerModal
                open={true}
                hide={() => setOrgPickerOpen(false)}
                onPick={(id) => {
                    act.local.setFilter(filterKey, String(id));
                    setOrgPickerOpen(false);
                }}
            />}
        </>;
    }

    return <FilterPopover isOpen={isOpen} filterKey={filterKey} closePopup={closePopup}>
        <FilterPopoverContent>
            <FilterConfiguration filterKey={filterKey} closeConfiguration={closePopup} />
        </FilterPopoverContent>
        <FilterPopoverWrapped>
            <div className="pill" onClick={() => setIsOpen(!isOpen)}>
                {pillBody}
            </div>
        </FilterPopoverWrapped>
    </FilterPopover>
}